/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef } from 'react';

import { css, cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import EditIcon from '@atlaskit/icon/core/edit';
import type CardClient from '@atlaskit/link-provider/client';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from '../../common/messages';
import { ManagedBlockPromptEditor } from './ManagedBlockPromptEditor';
import type { SmartCardProviderProduct } from './sources/smart-card-product';

const labelStyles = css({
	display: 'block',
	color: token('color.text.subtle'),
	marginBottom: token('space.075'),
});

const promptSectionStyles = css({
	borderTopColor: token('color.border'),
	borderTopStyle: 'solid',
	borderTopWidth: token('border.width'),
	paddingTop: token('space.150'),
});

const promptToggleStyles = css({
	appearance: 'none',
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	borderWidth: 0,
	backgroundColor: 'transparent',
	color: token('color.text.subtle'),
	cursor: 'pointer',
	font: 'inherit',
	paddingTop: 0,
	paddingRight: 0,
	paddingBottom: 0,
	paddingLeft: 0,
	textAlign: 'left',
	'&:focus-visible': {
		outlineColor: token('color.border.focused'),
		outlineOffset: token('space.050'),
		outlineStyle: 'solid',
		outlineWidth: token('border.width.focused'),
	},
});

const promptToggleTextStyles = css({
	marginLeft: token('space.075'),
});

const promptToggleChevronStyles = css({
	marginLeft: 'auto',
});

const promptBodyStyles = cssMap({
	root: {
		marginTop: token('space.100'),
	},
	error: {
		marginTop: token('space.100'),
	},
});

type PromptPanelBaseProps = {
	cardClient?: CardClient;
	defaultValue?: JSONDocNode;
	isDisabled?: boolean;
	isExpanded: boolean;
	onPromptADFChange: (prompt: JSONDocNode) => void;
	onPromptChange: (prompt: string) => void;
	promptEditorKey: string;
	promptValidationError: boolean;
	smartCardProviderProduct?: SmartCardProviderProduct;
};

type PromptPanelWithToggleProps = PromptPanelBaseProps & {
	onToggle: () => void;
	showToggle?: true;
};

type PromptPanelWithoutToggleProps = PromptPanelBaseProps & {
	onToggle?: never;
	showToggle: false;
};

type PromptPanelProps = PromptPanelWithToggleProps | PromptPanelWithoutToggleProps;

export const PromptPanel = (props: PromptPanelProps): JSX.Element => {
	const {
		isDisabled = false,
		cardClient,
		defaultValue,
		isExpanded,
		onPromptADFChange,
		onPromptChange,
		promptEditorKey,
		promptValidationError,
		smartCardProviderProduct,
	} = props;
	const intl = useIntl();
	const promptEditorFocusRef = useRef<(() => boolean) | undefined>(undefined);
	const promptErrorId = promptValidationError ? 'agent-managed-extension-prompt-error' : undefined;
	const promptLabel = intl.formatMessage(messages.customInstructionsLabel);

	useEffect(() => {
		if (isExpanded) {
			promptEditorFocusRef.current?.();
		}
	}, [isExpanded]);

	const promptBody = (
		<Box xcss={promptBodyStyles.root}>
			<span css={labelStyles}>{promptLabel}</span>
			<ManagedBlockPromptEditor
				key={promptEditorKey}
				ariaDescribedBy={promptErrorId}
				ariaLabel={promptLabel}
				autoFocus={isExpanded}
				cardClient={cardClient}
				defaultValue={defaultValue}
				isDisabled={isDisabled}
				isInvalid={promptValidationError}
				onADFChange={(inputValue) => {
					if (!isDisabled) {
						onPromptADFChange(inputValue);
					}
				}}
				onInputChange={(inputValue) => {
					if (!isDisabled) {
						onPromptChange(inputValue);
					}
				}}
				placeholder={intl.formatMessage(messages.customInstructionsPlaceholder)}
				setFocusHandler={(focus) => {
					promptEditorFocusRef.current = focus;
				}}
				smartCardProviderProduct={smartCardProviderProduct}
			/>
			{promptValidationError && (
				<Box id={promptErrorId} role="alert" xcss={promptBodyStyles.error}>
					<Text color="color.text.danger">{intl.formatMessage(messages.emptyPrompt)}</Text>
				</Box>
			)}
		</Box>
	);

	if (props.showToggle === false) {
		return promptBody;
	}

	return (
		<div css={promptSectionStyles}>
			<button
				aria-expanded={isExpanded}
				css={promptToggleStyles}
				disabled={isDisabled}
				onClick={props.onToggle}
				type="button"
			>
				<EditIcon label="" color="currentColor" />
				<span css={promptToggleTextStyles}>{intl.formatMessage(messages.promptToggleLabel)}</span>
				<span css={promptToggleChevronStyles}>
					{isExpanded ? (
						<ChevronUpIcon label="" color="currentColor" />
					) : (
						<ChevronDownIcon label="" color="currentColor" />
					)}
				</span>
			</button>
			{isExpanded && promptBody}
		</div>
	);
};
